import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { ArrowLeft, Calendar, MapPin, AlertCircle } from 'lucide-react';

interface EventItem {
  starts_at: string;
  ends_at: string | null;
  all_day: boolean;
  title: string;
  location: string | null;
  grade_level: number | null;
  scope: string;
  source: string;
  is_exam: boolean;
  class_normalized?: string | null;
  kind?: string;
  status?: string;
  subject_code?: string | null;
  study_content?: string | null;
  description?: string | null;
  material_url?: string | null;
  teacher_name?: string | null;
}
interface EventsResponse { events: EventItem[]; user_grade: number | null; mode: string; }

function groupByMonth(events: EventItem[]): Map<string, EventItem[]> {
  const map = new Map<string, EventItem[]>();
  for (const e of events) {
    const d = new Date(e.starts_at);
    const key = d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(e);
  }
  return map;
}

function fmtTime(iso: string, allDay: boolean): string {
  if (allDay) return '';
  return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}
function fmtDay(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
}

// Color schemes per scope/source. Teacher-posted exams stand out; exams are red-tinted.
function scopeStyle(e: EventItem): { badge: string; border: string; dateBg: string; label: string } {
  if (e.source === 'teacher') {
    return {
      badge: 'bg-orange-500/20 text-orange-200 border-orange-500/40',
      border: 'border-orange-500/60',
      dateBg: 'bg-orange-500/20 text-orange-200',
      label: 'Lehrer/in',
    };
  }
  if (e.is_exam) {
    return {
      badge: 'bg-red-500/20 text-red-200 border-red-500/40',
      border: 'border-red-500/60',
      dateBg: 'bg-red-500/20 text-red-200',
      label: 'Prüfung',
    };
  }
  if (e.scope === 'class') {
    return {
      badge: 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40',
      border: 'border-emerald-500/60',
      dateBg: 'bg-emerald-500/20 text-emerald-200',
      label: 'Klasse',
    };
  }
  if (e.scope === 'other_grade') {
    return {
      badge: 'bg-gray-600/30 text-gray-300 border-gray-600/40',
      border: 'border-gray-600/50',
      dateBg: 'bg-gray-700 text-gray-300',
      label: 'Andere Jgst.',
    };
  }
  return {
    badge: 'bg-purple-500/20 text-purple-200 border-purple-500/40',
    border: 'border-purple-500/40',
    dateBg: 'bg-purple-500/20 text-purple-200',
    label: 'Allgemein',
  };
}

const TerminePage: React.FC = () => {
  const [events, setEvents] = useState<EventItem[]>([]);
  const [userGrade, setUserGrade] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [onlyExams, setOnlyExams] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api<EventsResponse>(`/api/events?grades=${showAll ? 'all' : 'own'}`);
        setEvents(r.events);
        setUserGrade(r.user_grade);
      } catch (err: any) {
        setError(err?.message || 'Konnte Termine nicht laden');
      }
    })();
  }, [showAll]);

  const filtered = useMemo(() => onlyExams ? events.filter(e => e.is_exam) : events, [events, onlyExams]);
  const byMonth = useMemo(() => groupByMonth(filtered), [filtered]);

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <header className="bg-gray-800 border-b border-gray-700 px-4 sm:px-6 py-4 flex items-center gap-3 flex-wrap">
        <Link to="/dashboard" className="text-gray-400 hover:text-white flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
        <Calendar className="w-6 h-6 text-purple-400" />
        <h1 className="text-xl font-semibold">Termine</h1>
        <div className="ml-auto flex items-center gap-4 text-sm flex-wrap">
          <label className="flex items-center gap-2 text-gray-300">
            <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} className="accent-blue-500" />
            Alle Jahrgangsstufen
          </label>
          <label className="flex items-center gap-2 text-gray-300">
            <input type="checkbox" checked={onlyExams} onChange={e => setOnlyExams(e.target.checked)} className="accent-red-500" />
            Nur Prüfungen
          </label>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 sm:p-6">
        <div className="mb-4 flex flex-wrap gap-3 text-xs">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded border bg-emerald-500/20 text-emerald-200 border-emerald-500/40">Klasse</span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded border bg-red-500/20 text-red-200 border-red-500/40">Prüfung</span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded border bg-orange-500/20 text-orange-200 border-orange-500/40">Lehrer/in</span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded border bg-purple-500/20 text-purple-200 border-purple-500/40">Allgemein</span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded border bg-gray-600/30 text-gray-300 border-gray-600/40">Andere Jgst.</span>
        </div>
        {userGrade !== null && !showAll && (
          <p className="text-sm text-gray-400 mb-4">Zeige nur Termine deiner Jahrgangsstufe ({userGrade}) + allgemeine Termine.</p>
        )}
        {error && <p className="mb-4 text-red-400 text-sm">{error}</p>}
        {filtered.length === 0 ? (
          <p className="text-gray-400">Keine Termine in dieser Ansicht.</p>
        ) : (
          <div className="space-y-8">
            {Array.from(byMonth.entries()).map(([month, items]) => (
              <section key={month}>
                <h2 className="text-sm uppercase tracking-wider text-gray-500 mb-3">{month}</h2>
                <ul className="space-y-2">
                  {items.map((e, i) => {
                    const s = scopeStyle(e);
                    return (
                      <li key={i} className={`bg-gray-800 border-l-4 ${s.border} border-y border-r border-gray-700 rounded-lg p-4 flex items-start gap-4`}>
                        <div className={`${s.dateBg} rounded-md px-3 py-2 text-center min-w-[80px]`}>
                          <div className="text-xs">{fmtDay(e.starts_at)}</div>
                          {!e.all_day && <div className="text-sm font-mono">{fmtTime(e.starts_at, false)}</div>}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            {e.is_exam && <AlertCircle className="w-4 h-4 text-red-400" />}
                            <span className="font-medium">{e.title}</span>
                            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${s.badge}`}>{s.label}</span>
                            {e.status && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-gray-700 text-gray-300">{e.status}</span>}
                          </div>
                          {e.description && <p className="text-sm text-gray-400 mt-1 whitespace-pre-wrap">{e.description}</p>}
                          {e.study_content && (
                            <div className="mt-2 p-2 rounded bg-gray-900/60 border border-gray-700 text-sm text-gray-300">
                              <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Lerninhalte</div>
                              <div className="whitespace-pre-wrap">{e.study_content}</div>
                            </div>
                          )}
                          <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-400">
                            {e.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.location}</span>}
                            {e.subject_code && <span className="bg-gray-700 px-1.5 py-0.5 rounded">{e.subject_code}</span>}
                            {e.grade_level !== null && !e.class_normalized && <span className="bg-purple-500/20 text-purple-200 px-1.5 py-0.5 rounded">Jgst. {e.grade_level}</span>}
                            {e.class_normalized && <span className="bg-emerald-500/20 text-emerald-200 px-1.5 py-0.5 rounded">Klasse {e.class_normalized}</span>}
                            {e.teacher_name && <span>👩‍🏫 {e.teacher_name}</span>}
                            {e.material_url && (
                              <a href={e.material_url} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300">📎 Material</a>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default TerminePage;
